import { useState } from "react";
import { Button, Checkbox, Collapsible, Divider, Icon, RadioButton, Text, Tooltip } from "@shopify/polaris";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import SwatchesSelector from "../../../components/SwatchesModal/SwatchesSelector";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import styles from "./CommonFields.module.css";
import { AdjustIcon, InfoIcon, VariantIcon } from "@shopify/polaris-icons";
import { AvailableVolumeTemplates } from "../constants";
import { Field } from "@shopify/react-form";
import MantleDoc, { mantleDocHandles } from "../../../components/MantleDoc";

interface SwatchesSelectorWrapperProps {
    dispatchBundleConfig: (action: any) => void;
    show_swatches: any;
    swatchOptions: any;
    swatchesProductsMap: any;
    swatchShape: any;
    swatchSize: any;
    productSource: any;
    bundleType: string;
    bundleConfig: any;
    SwatchesBundleConfigTouchedRef: React.MutableRefObject<Record<string, string>>;
    show_variant_selector?: Field<boolean>;
    selected_template_volume?: Field<string>;
    setOpenDefaultVariantSelector?: (val: boolean) => void;
}

const SwatchesSelectorWrapper = ({
    dispatchBundleConfig,
    show_swatches,
    swatchOptions,
    swatchesProductsMap,
    swatchShape,
    swatchSize,
    productSource,
    bundleType,
    bundleConfig,
    SwatchesBundleConfigTouchedRef,
    show_variant_selector,
    selected_template_volume,
    setOpenDefaultVariantSelector,
}: SwatchesSelectorWrapperProps) => {
    const [expandVariantSelector, setExpandVariantSelector] = useState(false);
    const [open, setOpen] = useState(false);
    const { trackFormSections } = useBundleMixpanelHook();

    const updateSwatchField = (key: any, val: any) => {
        dispatchBundleConfig({ key, payload: val });

        switch (key) {
            case "swatchShape":
                swatchShape.onChange(val);
                break;
            case "swatchSize":
                swatchSize.onChange(val);
                break;
            case "swatchOptions":
                swatchOptions.onChange(val);
                break;
            case "swatchesProductsMap":
                swatchesProductsMap.onChange(val);
                break;
        }
    };

    const handleSwatchApply = (updated: any) => {
        (["swatchShape", "swatchSize", "swatchOptions", "swatchesProductsMap"] as const).forEach((key) => {
            const val = updated[key];

            if (val !== undefined) {
                updateSwatchField(key, val);
            }
        });

        if (Object.keys(SwatchesBundleConfigTouchedRef.current).length > 0) {
            (["swatchOptions", "swatchesProductsMap"] as const).forEach((key) => {
                switch (key) {
                    case "swatchOptions":
                        swatchOptions.onChange((prev: any) => [...prev]);
                        break;
                    case "swatchesProductsMap":
                        swatchesProductsMap.onChange((prev: any) => ({ ...prev }));
                        break;
                }
            });
        }
    };

    return (
        <>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandVariantSelector((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Variant Selector ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <MantleDoc handle={mantleDocHandles[bundleType].setUpVariantSelector} title="Variant selector" />

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandVariantSelector ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>

            <Collapsible
                open={expandVariantSelector}
                id="variant-selector-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <div style={{ paddingBottom: "16px" }}>
                    <Text as="p" variant="bodyMd" tone="subdued">
                        Choose how customers pick product options inside the bundle.
                    </Text>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            marginTop: "16px",
                            gap: "12px",
                        }}
                    >
                        <RadioButton
                            label="Dropdown"
                            helpText="Customers select options from a dropdown list."
                            id="dropdown_selector"
                            checked={!show_swatches.value}
                            onChange={() => {
                                dispatchBundleConfig({
                                    payload: false,
                                    key: "show_swatches",
                                });
                                show_swatches.onChange(false);
                            }}
                        />
                        <RadioButton
                            label="Swatches"
                            helpText="Show variants as image swatches, color swatches, labels, or dropdown swatches."
                            id="swatches_selector"
                            checked={show_swatches.value}
                            onChange={() => {
                                dispatchBundleConfig({
                                    payload: true,
                                    key: "show_swatches",
                                });
                                show_swatches.onChange(true);
                            }}
                        />
                    </div>

                    {show_swatches.value && (
                        <div style={{ marginTop: "16px" }}>
                            <Button onClick={() => setOpen((prev) => !prev)} icon={VariantIcon}>
                                Set up swatches
                            </Button>
                        </div>
                    )}

                    {open && bundleConfig && (
                        <SwatchesSelector
                            productSource={productSource}
                            open={open}
                            setOpen={setOpen}
                            bundleConfig={bundleConfig}
                            onApply={handleSwatchApply}
                            SwatchesBundleConfigTouchedRef={SwatchesBundleConfigTouchedRef}
                        />
                    )}

                    {show_variant_selector && selected_template_volume && setOpenDefaultVariantSelector && (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                marginTop: "16px",
                                gap: "12px",
                            }}
                        >
                            <Checkbox
                                label={
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        Let Customers Choose Different Variants for Each Item
                                        <div>
                                            <Tooltip width="wide" content={"Only available for vertical layouts"}>
                                                <Icon source={InfoIcon} tone="subdued" />
                                            </Tooltip>
                                        </div>
                                    </div>
                                }
                                {...show_variant_selector}
                                value=""
                                checked={show_variant_selector.value}
                                onChange={(val) => {
                                    dispatchBundleConfig({
                                        payload: val,
                                        key: "show_variant_selector",
                                    });
                                    show_variant_selector.onChange(val);
                                }}
                                disabled={
                                    selected_template_volume.value == AvailableVolumeTemplates.HORIZONTAL_BASIC ||
                                    selected_template_volume.value == AvailableVolumeTemplates.HORIZONTAL_MODERN
                                }
                            />

                            <div className={styles.inputWrapper}>
                                <Button
                                    variant="secondary"
                                    icon={AdjustIcon}
                                    onClick={() => {
                                        setOpenDefaultVariantSelector(true);
                                    }}
                                    disabled={
                                        selected_template_volume.value == AvailableVolumeTemplates.HORIZONTAL_BASIC ||
                                        selected_template_volume.value == AvailableVolumeTemplates.HORIZONTAL_MODERN ||
                                        show_variant_selector.value == false
                                    }
                                >
                                    Set default variants
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </Collapsible>
        </>
    );
};

export default SwatchesSelectorWrapper;
